import { WalletReadyState } from "@solana/wallet-adapter-base";
import { useWallet } from "@solana/wallet-adapter-react";
import { isMobile } from "react-device-detect";
import { createPortal } from "react-dom";
import {
    Fragment,
    useState,
    useRef,
    useMemo,
    useCallback,
    useLayoutEffect,
    useEffect,
    useContext,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useSolanaWalletModal } from "../Contexts/useSolanaWalletModal";
import { useAuth } from "../Contexts/useAuth";
import { GlobalContext } from "../Contexts/GlobalContext";
import { Spinner } from "./Spinner";

import { classNames } from "../Utils/helpers";

const FADE_DURATION = 200;
const PHANTOM_URL = process.env.REACT_APP_PHANTOM_URL;
const TNC_URL = process.env.REACT_APP_TNC_URL;
const PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL;
const CONNECT_FAILED_MSG = "Connection failed. Please try again later.";
const SIGN_MSG_FAILED_MSG = "Sign message failed. Please try again later.";

function WalletButton({ handleClick, wallet, disabled, isProcessing }) {
    return (
        <li>
            <button
                disabled={disabled}
                onClick={handleClick}
                type="button"
                className="inline-flex w-full items-center justify-start rounded-lg border border-transparent bg-gray-50 py-2 pl-3 pr-4 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
                <i className="mr-3 h-8 w-8">
                    <img
                        src={wallet.adapter.icon}
                        alt={`${wallet.adapter.name} icon`}
                    />
                </i>
                <span className="text-base font-bold text-gray-800">
                    {wallet.adapter.name}
                </span>
                {isProcessing ? (
                    <div className="ml-auto flex flex-row">
                        <Spinner color="text-gray-500" size="h-5 w-5" />
                        <span className="sr-only">Connecting</span>
                    </div>
                ) : (
                    <span className="ml-auto text-sm font-normal text-gray-500">
                        Detected
                    </span>
                )}
            </button>
        </li>
    );
}

export function SolanaWalletModal() {
    const ref = useRef(null);
    const { wallets, wallet, connected, select, connect } = useWallet();
    const { authed, doAuth } = useAuth();
    const { setVisible } = useSolanaWalletModal();
    const [show, setShow] = useState(false);
    const [isAuthing, setIsAuthing] = useState(false);
    const [error, setError] = useState(null);
    const { walletErr, setWalletErr } = useContext(GlobalContext);

    const installedWallets = useMemo(() => {
        const filtered = [];
        for (const wallet of wallets) {
            if (wallet.readyState === WalletReadyState.Installed) {
                filtered.push(wallet);
            }
        }

        return filtered;
    }, [wallets]);

    const hideModal = useCallback(() => {
        setIsAuthing(false);
        setShow(false);
        setTimeout(() => setVisible(false), FADE_DURATION);
    }, [setVisible]);

    const handleClose = useCallback(
        (event) => {
            if (isAuthing) return;
            if (event instanceof Event) event.preventDefault();
            hideModal();
        },
        [hideModal, isAuthing]
    );

    const handleWalletClick = useCallback(
        (event, walletName) => {
            event.preventDefault();
            const notChange = wallet && walletName === wallet.adapter.name;
            if (notChange && authed) {
                hideModal();
                return;
            }
            setIsAuthing(true);
            console.log("Solana Wallet Modal:", connected, notChange);
            if (notChange && !connected) {
                connect();
                return;
            } else {
                select(walletName);
                return;
            }
        },
        [wallet, connected, authed, select, connect, hideModal]
    );

    useEffect(() => {
        const errName = walletErr?.name;
        if (isAuthing && errName === "WalletConnectionError") {
            setWalletErr(null);
            setIsAuthing(false);
        }
    }, [isAuthing, walletErr, setWalletErr]);

    useEffect(() => {
        let ignore = false;
        if (isAuthing && connected) {
            setTimeout(async () => {
                if (ignore) return;
                try {
                    await doAuth();
                    hideModal();
                } catch (error) {
                    console.log("Original Auth Error", error);
                    switch (error.name) {
                        case "WalletConnectionError":
                            error.message = CONNECT_FAILED_MSG;
                            break;
                        case "WalletSignMessageError":
                            error.message = SIGN_MSG_FAILED_MSG;
                            break;
                        default:
                            break;
                    }
                    setError(error);
                    setIsAuthing(false);
                }
            }, 100);
        }

        return () => {
            ignore = true;
        };
    }, [isAuthing, connected, doAuth, hideModal]);

    const handleTabKey = useCallback(
        (event) => {
            const node = ref.current;
            if (!node) return;

            // here we query all focusable elements
            const focusableElements = node.querySelectorAll("button");
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const firstElement = focusableElements?.[0];
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const lastElement =
                focusableElements?.[focusableElements.length - 1];

            if (event.shiftKey) {
                // if going backward by pressing tab and firstElement is active, shift focus to last focusable element
                if (document.activeElement === firstElement) {
                    lastElement.focus();
                    event.preventDefault();
                }
            } else {
                // if going forward by pressing tab and lastElement is active, shift focus to first focusable element
                if (document.activeElement === lastElement) {
                    firstElement.focus();
                    event.preventDefault();
                }
            }
        },
        [ref]
    );

    useLayoutEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                hideModal();
            } else if (event.key === "Tab") {
                handleTabKey(event);
            }
        };

        // Get original overflow
        const { overflow } = window.getComputedStyle(document.body);
        // Hack to enable fade in animation after mount
        setTimeout(() => setShow(true), 0);
        // Prevent scrolling on mount
        document.body.style.overflow = "hidden";
        // Listen for keydown events
        window.addEventListener("keydown", handleKeyDown, false);

        return () => {
            // Re-enable scrolling when component unmounts
            document.body.style.overflow = overflow;
            window.removeEventListener("keydown", handleKeyDown, false);
        };
    }, [hideModal, handleTabKey]);

    if (isMobile) {
        return createPortal(
            <MobileWarningModal
                show={show}
                hideModal={hideModal}
                handleClose={handleClose}
            />,
            document.body
        );
    }

    return createPortal(
        <Transition.Root show={show} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter={classNames("ease-out", `duration-${FADE_DURATION}`)}
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave={classNames("ease-in", `duration-${FADE_DURATION}`)}
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter={classNames(
                                "ease-out",
                                `duration-${FADE_DURATION}`
                            )}
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave={classNames(
                                "ease-in",
                                `duration-${FADE_DURATION}`
                            )}
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                    <button
                                        type="button"
                                        className={classNames(
                                            isAuthing && "hidden",
                                            "rounded-md bg-white text-gray-700 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        )}
                                        onClick={handleClose}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon
                                            className="h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                                <div className="text-left">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-7 text-gray-800"
                                    >
                                        Connect to a Solana Wallet
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            Connect a wallet and sign your
                                            signature in the wallet extension to
                                            proceed.
                                        </p>
                                    </div>
                                    {error !== null && (
                                        <div className="mt-6 rounded bg-orange-50 p-4">
                                            <p className="text-sm leading-5 text-orange-600">
                                                {error.message}
                                            </p>
                                        </div>
                                    )}
                                    {installedWallets.length === 0 && (
                                        <div className="mt-6 flex flex-col justify-center rounded bg-orange-50 p-4">
                                            <p className="text-sm leading-5 text-orange-600">
                                                It looks like you don’t have a
                                                Solana wallet installed in your
                                                browser yet. Do you want to
                                                install{" "}
                                                <a
                                                    className="font-bold text-orange-600 underline"
                                                    href={PHANTOM_URL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Phantom
                                                </a>
                                                ?
                                            </p>
                                        </div>
                                    )}
                                    <ul ref={ref} className="mt-6 space-y-3">
                                        {installedWallets.map((item) => {
                                            return (
                                                <WalletButton
                                                    key={item.adapter.name}
                                                    handleClick={(event) => {
                                                        handleWalletClick(
                                                            event,
                                                            item.adapter.name
                                                        );
                                                    }}
                                                    wallet={item}
                                                    disabled={isAuthing}
                                                    isProcessing={
                                                        wallet &&
                                                        wallet.adapter.name ===
                                                            item.adapter.name &&
                                                        isAuthing
                                                    }
                                                />
                                            );
                                        })}
                                    </ul>
                                    <div className="mt-6">
                                        <p className="text-gray500 text-xs font-medium">
                                            By connecting a wallet, you agree
                                            and accept our{" "}
                                            <a
                                                className="font-bold text-orange-600 underline"
                                                href={TNC_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Terms of Service
                                            </a>{" "}
                                            and consent to our{" "}
                                            <a
                                                className="font-bold text-orange-600 underline"
                                                href={PRIVACY_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Privacy Policy
                                            </a>
                                            .
                                        </p>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>,
        document.body
    );
}

function MobileWarningModal({ show, hideModal, handleClose }) {
    return (
        <Transition.Root show={show} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={hideModal}>
                <Transition.Child
                    as={Fragment}
                    enter={classNames("ease-out", `duration-${FADE_DURATION}`)}
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave={classNames("ease-in", `duration-${FADE_DURATION}`)}
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter={classNames(
                                "ease-out",
                                `duration-${FADE_DURATION}`
                            )}
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave={classNames(
                                "ease-in",
                                `duration-${FADE_DURATION}`
                            )}
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div className="absolute top-0 right-0 pt-4 pr-4 sm:block">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-700 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        onClick={handleClose}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon
                                            className="h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                                <div className="text-left">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-7 text-gray-800"
                                    >
                                        Mobile not supported
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            The site is not currently supported
                                            on mobiles. We recommend you
                                            visiting it on a desktop or laptop{" "}
                                            <a
                                                className="font-bold text-orange-600 underline"
                                                href="https://www.google.com/chrome/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Chrome
                                            </a>{" "}
                                            browser.
                                        </p>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}